/**
 * Hauptmenue (Masterprompt Abschnitt 88, Produktentscheidung vom 13.10.2026).
 *
 * Es ist das Erste, was man sieht. Fuenf Dinge, mehr nicht:
 *
 *   Singleplayer | Multiplayer | Settings | Subscribe to Playtest | Discord
 *
 * Bis zum 13.10.2026 lag alles auf einer Seite: Heldenwahl, vier Startknoepfe,
 * Bot-Einstellungen und Codefeld untereinander. Das war auf einem Handy im
 * Querformat eine Rolltreppe, und die wichtigste Frage - "allein oder gegen
 * jemanden?" - stand nirgends.
 *
 * Jetzt fuehrt jeder Knopf auf einen eigenen Bildschirm. WAS ein Knopf
 * ausloest, steht in menuModel.ts und ist dort ohne Bild pruefbar; hier steht
 * nur, wie es aussieht und wohin man klickt.
 *
 * Der Zugangscode des geschlossenen Playtests kommt VOR diesem Menue
 * (Zugangstor, 04.10.2026) - wer keinen hat, sieht es nie.
 */

import { useState } from 'react';
import { BOT_DIFFICULTIES, BOT_PROFILES, DEFAULT_HERO_ID } from '@dueline/game-data';
import {
  DISCORD_INVITE_URL,
  MATCH_CODE_LENGTH,
  isValidMatchCode,
  normalizeMatchCode,
} from '@dueline/shared';
import { t } from '../i18n/index.js';
import type { JoinMode, MatchSnapshot } from '../net/MatchConnection.js';
import { ZUGANG_FEHLER } from '../net/zugangscode.js';
import { LineboundWahl } from './LineboundWahl.js';
import { isFullscreen, mausVorhanden, toggleFullscreen, vollbildMoeglich } from './fullscreen.js';
import type { Steuerung } from '../input/mausSteuerung.js';
import { HomescreenHilfe } from './StartbildschirmHinweis.js';
import { MenueHintergrund } from './MenueHintergrund.js';
import { MENUE_LOGO } from './menueBilder.js';
import {
  multiplayerStart,
  singleplayerStart,
  zurueckVon,
  type MenuBildschirm,
  type Startbefehl,
} from './menuModel.js';

/** Wohin "Subscribe to Playtest" fuehrt - die Seite der Homepage. */
const PLAYTEST_URL = 'https://dueline-game.com/playtest/';

interface Props {
  snapshot: MatchSnapshot;
  onStart: (mode: JoinMode, code: string, heroId: string, botHeroId?: string) => void;
  /** Ist der Ton gerade aus? Kommt aus der App, damit es im Match gleich ist. */
  stumm: boolean;
  onToggleSound: () => void;
  /**
   * Die gewaehlte Steuerung (25.10.2026).
   *
   * Sie kommt aus der App und nicht von hier: Im Match gilt dieselbe
   * Einstellung, und gemerkt wird sie nur an einer Stelle.
   */
  steuerung: Steuerung;
  onSteuerung: (steuerung: Steuerung) => void;
}

export function MainMenu({
  snapshot,
  onStart,
  stumm,
  onToggleSound,
  steuerung,
  onSteuerung,
}: Props) {
  const [bildschirm, setBildschirm] = useState<MenuBildschirm>('start');
  const [heroId, setHeroId] = useState(DEFAULT_HERO_ID);
  const [botHeroId, setBotHeroId] = useState('');
  const [stufe, setStufe] = useState<(typeof BOT_DIFFICULTIES)[number]>('normal');
  const [code, setCode] = useState('');

  const verbindet = snapshot.status === 'connecting';

  /** Einen Startbefehl ausfuehren - oder nichts tun, wenn keiner kam. */
  const starte = (befehl: Startbefehl | null) => {
    if (!befehl) return;
    onStart(befehl.mode, befehl.code, befehl.heroId, befehl.botHeroId);
  };

  return (
    <div className="dl-menu dl-menu--bild">
      {/*
        Im Stil der Homepage (15.10.2026): ihr Kopfbild als Hintergrund, ihr
        Logo oben. Das Bild ist reine Kulisse - fuer Screenreader leer -
        und blendet weich ein, sobald es geladen ist; bis dahin steht das
        Menue schon auf dunklem Grund.
      */}
      <MenueHintergrund />

      <div className="dl-menu__inhalt">
        <header className="dl-menu__kopf">
          <h1 className="dl-menu__title">
            <picture>
              <source type="image/avif" srcSet={MENUE_LOGO.avif} sizes="min(640px, 60vw)" />
              <source type="image/webp" srcSet={MENUE_LOGO.webp} sizes="min(640px, 60vw)" />
              <img className="dl-menu__logo" src={MENUE_LOGO.ersatz} alt={t.menu.title} />
            </picture>
          </h1>
          {bildschirm === 'start' && <p className="dl-menu__claim">{t.menu.claim}</p>}
        </header>

        <div className="dl-menu__panel">
          {bildschirm === 'start' && (
            <div className="dl-menu__liste">
              <button
                type="button"
                className="dl-button dl-button--primary dl-menu__button"
                disabled={verbindet}
                onClick={() => setBildschirm('singleplayer')}
              >
                {t.menu.singleplayer}
                <span className="dl-menu__hint">{t.menu.singleplayerHint}</span>
              </button>

              <button
                type="button"
                className="dl-button dl-menu__button"
                disabled={verbindet}
                onClick={() => setBildschirm('multiplayer')}
              >
                {t.menu.multiplayer}
                <span className="dl-menu__hint">{t.menu.multiplayerHint}</span>
              </button>

              <button
                type="button"
                className="dl-button dl-menu__button"
                onClick={() => setBildschirm('settings')}
              >
                {t.menu.settings}
                <span className="dl-menu__hint">{t.menu.settingsHint}</span>
              </button>

              {/*
              Ein Verweis, kein Knopf: Er fuehrt aus dem Spiel heraus auf die
              Homepage. Neuer Tab mit rel="noopener noreferrer" - sonst kann
              die geoeffnete Seite auf das Spielfenster zugreifen.
            */}
              <a
                className="dl-button dl-menu__button dl-menu__button--link"
                href={PLAYTEST_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t.menu.subscribe}
                <span className="dl-menu__hint">{t.menu.subscribeHint}</span>
              </a>
            </div>
          )}

          {bildschirm === 'multiplayer' && (
            <MultiplayerBildschirm
              code={code}
              onCode={setCode}
              verbindet={verbindet}
              onStart={(art) => starte(multiplayerStart({ art, code, verbindet }))}
            />
          )}

          {bildschirm === 'singleplayer' && (
            <SingleplayerBildschirm
              heroId={heroId}
              onWaehleHero={setHeroId}
              botHeroId={botHeroId}
              onWaehleBot={setBotHeroId}
              stufe={stufe}
              onStufe={setStufe}
              verbindet={verbindet}
              onStart={() => starte(singleplayerStart({ heroId, botHeroId, stufe, verbindet }))}
            />
          )}

          {bildschirm === 'settings' && (
            <SettingsBildschirm
              stumm={stumm}
              onTon={onToggleSound}
              steuerung={steuerung}
              onSteuerung={onSteuerung}
            />
          )}

          {bildschirm !== 'start' && (
            <button
              type="button"
              className="dl-button dl-menu__zurueck"
              onClick={() => setBildschirm(zurueckVon(bildschirm))}
            >
              {t.menu.back}
            </button>
          )}

          {verbindet && <p className="dl-menu__status">{t.menu.connecting}</p>}

          {snapshot.status === 'error' && (
            <p className="dl-menu__status dl-menu__status--error">
              {uebersetzeFehler(snapshot.errorMessage)}
            </p>
          )}
        </div>
      </div>

      {/*
        Discord unten links in der Ecke (Produktentscheidung vom 13.10.2026) -
        aus dem Menue heraus, nicht mittendrin. Es ist der einzige Weg nach
        draussen, der immer sichtbar bleibt, und er buhlt dort mit keinem
        Startknopf um dieselbe Aufmerksamkeit.
      */}
      <a
        className="dl-menu__discordEcke"
        href={DISCORD_INVITE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <svg className="dl-menu__discordEckeIcon" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 5h16v11H9l-5 4Z" />
        </svg>
        {t.menu.discord}
      </a>
    </div>
  );
}

/** Quickmatch, Lobby erstellen, Lobby beitreten. */
function MultiplayerBildschirm({
  code,
  onCode,
  verbindet,
  onStart,
}: {
  code: string;
  onCode: (code: string) => void;
  verbindet: boolean;
  onStart: (art: 'quick' | 'create' | 'join') => void;
}) {
  const [codeOffen, setCodeOffen] = useState(false);

  return (
    <>
      <div className="dl-menu__liste">
        <button
          type="button"
          className="dl-button dl-button--primary dl-menu__button"
          disabled={verbindet}
          onClick={() => onStart('quick')}
        >
          {t.menu.quick}
          <span className="dl-menu__hint">{t.menu.quickHint}</span>
        </button>

        <button
          type="button"
          className="dl-button dl-menu__button"
          disabled={verbindet}
          onClick={() => onStart('create')}
        >
          {t.menu.create}
          <span className="dl-menu__hint">{t.menu.createHint}</span>
        </button>

        <button
          type="button"
          className="dl-button dl-menu__button"
          disabled={verbindet}
          onClick={() => setCodeOffen((offen) => !offen)}
        >
          {t.menu.join}
          <span className="dl-menu__hint">{t.menu.joinHint}</span>
        </button>
      </div>

      {codeOffen && (
        <form
          className="dl-menu__code"
          onSubmit={(event) => {
            event.preventDefault();
            onStart('join');
          }}
        >
          <input
            className="dl-menu__input"
            value={code}
            onChange={(event) => onCode(normalizeMatchCode(event.target.value))}
            placeholder={'X'.repeat(MATCH_CODE_LENGTH)}
            inputMode="text"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            aria-label={t.menu.codeLabel}
          />
          <button
            type="submit"
            className="dl-button dl-button--primary"
            disabled={!isValidMatchCode(code) || verbindet}
          >
            {t.menu.joinAction}
          </button>
        </form>
      )}

      {/*
        Hier steht KEINE Linebound-Wahl mehr (Schritt 2, 13.10.2026): Gegen
        Menschen waehlt man in der Lobby, wo eine Minute Zeit dafuer ist.
      */}
      <p className="dl-menu__hinweis">{t.menu.pickInLobby}</p>
    </>
  );
}

/** Gegen den Testgegner: seine Staerke, sein Linebound, der eigene. */
function SingleplayerBildschirm({
  heroId,
  onWaehleHero,
  botHeroId,
  onWaehleBot,
  stufe,
  onStufe,
  verbindet,
  onStart,
}: {
  heroId: string;
  onWaehleHero: (id: string) => void;
  botHeroId: string;
  onWaehleBot: (id: string) => void;
  stufe: (typeof BOT_DIFFICULTIES)[number];
  onStufe: (stufe: (typeof BOT_DIFFICULTIES)[number]) => void;
  verbindet: boolean;
  onStart: () => void;
}) {
  return (
    <>
      <div className="dl-menu__abschnitt">
        <div className="dl-menu__abschnittTitel">{t.menu.heroTitle}</div>
        <LineboundWahl gewaehlt={heroId} onWaehle={onWaehleHero} gesperrt={verbindet} />
      </div>

      <div className="dl-menu__abschnitt">
        <div className="dl-menu__abschnittTitel">{t.menu.botTitle}</div>
        <div className="dl-menu__stufen">
          {BOT_DIFFICULTIES.map((wahl) => (
            <button
              key={wahl}
              type="button"
              className={'dl-linebound' + (wahl === stufe ? ' dl-linebound--active' : '')}
              disabled={verbindet}
              aria-pressed={wahl === stufe}
              onClick={() => onStufe(wahl)}
            >
              <strong>{BOT_PROFILES[wahl].name}</strong>
              <span>{t.menu.botLevels[wahl]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="dl-menu__abschnitt">
        <div className="dl-menu__abschnittTitel">{t.menu.botHeroTitle}</div>
        <LineboundWahl gewaehlt={botHeroId} onWaehle={onWaehleBot} gesperrt={verbindet} mitZufall />
      </div>

      <button
        type="button"
        className="dl-button dl-button--primary dl-menu__start"
        disabled={verbindet}
        onClick={onStart}
      >
        {t.menu.startGame}
      </button>
    </>
  );
}

/**
 * Ton und Vollbild - schon vor dem Match einstellbar.
 *
 * Dieselben zwei Fragen stehen im Match oben rechts. Hier sind sie nur
 * frueher erreichbar; die Antworten gelten fuer beide Orte, weil der Ton in
 * der App liegt und das Vollbild im Browser.
 */
function SettingsBildschirm({
  stumm,
  onTon,
  steuerung,
  onSteuerung,
}: {
  stumm: boolean;
  onTon: () => void;
  steuerung: Steuerung;
  onSteuerung: (steuerung: Steuerung) => void;
}) {
  const [vollbild, setVollbild] = useState(isFullscreen);

  return (
    <div className="dl-menu__abschnitt">
      <button
        type="button"
        className="dl-button dl-menu__button"
        aria-pressed={!stumm}
        onClick={onTon}
      >
        {stumm ? t.sound.off : t.sound.on}
        <span className="dl-menu__hint">{t.menu.soundHint}</span>
      </button>

      {/*
        Der Knopf nur dort, wo er auch etwas tut (15.10.2026).

        Auf dem iPhone erlaubt der Browser kein Vollbild - der Knopf stand
        dort bisher und passierte nichts, wenn man ihn drueckte. Das ist
        schlimmer als kein Knopf: Man haelt das Spiel fuer kaputt.
      */}
      {vollbildMoeglich() && (
        <button
          type="button"
          className="dl-button dl-menu__button"
          aria-pressed={vollbild}
          onClick={() => {
            void toggleFullscreen().then(() => setVollbild(isFullscreen()));
          }}
        >
          {vollbild ? t.fullscreen.exit : t.fullscreen.enter}
          <span className="dl-menu__hint">{t.menu.fullscreenHint}</span>
        </button>
      )}

      {/*
        Die zweite Steuerung (Produktentscheidung vom 25.10.2026).

        Nur dort, wo es eine Maus und eine Tastatur gibt: Am Handy waere
        der Knopf eine Falle - er wuerde die Bedienung abschalten, die man
        dort ueberhaupt hat. Gefragt wird die EINGABE und nicht das Geraet;
        ein Laptop mit Touchscreen ist beides.
      */}
      {mausVorhanden() && (
        <button
          type="button"
          className="dl-button dl-menu__button"
          aria-pressed={steuerung === 'maus'}
          onClick={() => onSteuerung(steuerung === 'maus' ? 'tasten' : 'maus')}
        >
          {steuerung === 'maus' ? t.controls.mouse : t.controls.keyboard}
          <span className="dl-menu__hint">
            {steuerung === 'maus' ? t.controls.mouseHint : t.controls.keyboardHint}
          </span>
        </button>
      )}

      {/*
        Der Weg ueber den Home-Bildschirm - dauerhaft und auf JEDEM
        Handy (Robin, 15.10.2026: "damit auch andere Browser bzw. Handys
        das trotzdem machen koennen").
      */}
      <HomescreenHilfe />
    </div>
  );
}

/**
 * Macht aus einer technischen Fehlermeldung einen verstaendlichen Satz.
 *
 * Alles, was wir nicht kennen, wird im Wortlaut gezeigt - eine falsche
 * Vermutung waere schlimmer als eine sperrige Meldung.
 */
function uebersetzeFehler(meldung: string | null): string {
  if (!meldung) return t.menu.errors.unknown;

  const text = meldung.toLowerCase();
  if (text.includes('not found') || text.includes('no rooms')) return t.menu.errors.noMatch;
  if (text.includes('locked') || text.includes('full')) return t.menu.errors.full;
  if (text.includes(ZUGANG_FEHLER.fehlt)) return t.menu.errors.accessRequired;
  if (text.includes(ZUGANG_FEHLER.falsch)) return t.menu.errors.accessInvalid;
  if (text.includes(ZUGANG_FEHLER.geschlossen)) return t.menu.errors.accessClosed;
  if (text.includes('invalid_code')) return t.menu.errors.invalidCode;
  if (text.includes('protocol')) return t.menu.errors.protocol;
  return meldung;
}
